import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

// Generic dropdown menu rendered in a portal so it escapes overflow containers.
// Props:
//  trigger: (toggle, isOpen) => ReactNode
//  items: [{ label, icon, onClick, tone?: "default"|"danger", divider?, disabled?, hint? }]
//  align: "left" | "right"
export default function DropdownMenu({
  trigger,
  items = [],
  align = "right",
  menuClassName = "",
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, right: 0, maxHeight: 320, placement: "bottom" });
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  const recompute = () => {
    if (!triggerRef.current) return;
    const r = triggerRef.current.getBoundingClientRect();
    const margin = 8;
    const gap = 6;
    const spaceBelow = window.innerHeight - r.bottom - margin;
    const spaceAbove = r.top - margin;
    const menuHeight = menuRef.current?.offsetHeight || 0;
    // Flip upward if the menu wouldn't fit below but would fit better above.
    const placeAbove =
      menuHeight > spaceBelow && spaceAbove > spaceBelow;
    const placement = placeAbove ? "top" : "bottom";
    const top = placeAbove ? Math.max(margin, r.top - gap - menuHeight) : r.bottom + gap;
    const maxHeight = Math.max(160, placeAbove ? spaceAbove - gap : spaceBelow - gap);
    setPos({ top, left: r.left, right: r.right, maxHeight, placement });
  };

  useLayoutEffect(() => {
    if (open) {
      // Measure twice: first with initial guess, then with actual height.
      recompute();
      requestAnimationFrame(recompute);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (
        triggerRef.current?.contains(e.target) ||
        menuRef.current?.contains(e.target)
      )
        return;
      setOpen(false);
    };
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    const onScroll = (e) => {
      // Don't close if the scroll happened inside the menu itself.
      if (menuRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", recompute);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", recompute);
    };
  }, [open]);

  const toggle = () => setOpen((o) => !o);

  // Align: menu right-edge matches trigger right-edge (when align="right").
  // On mobile, use left+right insets instead of just right-anchoring,
  // so the menu never overflows either edge of the viewport.
  const menuStyle = {
    top: pos.top,
    left: 8,
    right: 8,
    maxHeight: pos.maxHeight,
  };
  // On wider screens, constrain to auto-width anchored to the trigger.
  if (typeof window !== "undefined" && window.innerWidth >= 480) {
    delete menuStyle.left;
    delete menuStyle.right;
    if (align === "right") {
      menuStyle.right = Math.max(8, window.innerWidth - pos.right);
    } else {
      menuStyle.left = Math.max(8, pos.left);
    }
  }

  return (
    <>
      <span ref={triggerRef} className="inline-flex">
        {trigger(toggle, open)}
      </span>
      {open &&
        createPortal(
          <div
            ref={menuRef}
            style={menuStyle}
            className={`fixed z-50 min-w-[12rem] overflow-y-auto rounded-lg border border-gray-200 bg-white p-1 shadow-lg dark:border-gray-700 dark:bg-gray-900 ${menuClassName}`}
            role="menu"
          >
            {items.map((it, idx) => {
              if (it.divider) {
                return <div key={`d-${idx}`} className="my-1 h-px bg-gray-100 dark:bg-gray-800" />;
              }
              const Icon = it.icon;
              const toneCls =
                it.tone === "danger"
                  ? "text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950"
                  : "text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-800";
              return (
                <button
                  key={it.label + idx}
                  type="button"
                  onClick={() => {
                    setOpen(false);
                    it.onClick?.();
                  }}
                  disabled={it.disabled}
                  className={`flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-sm transition-colors duration-150 disabled:opacity-40 ${toneCls}`}
                  role="menuitem"
                >
                  {Icon && <Icon className="h-4 w-4 flex-none" />}
                  <span className="min-w-0 flex-1 truncate">{it.label}</span>
                  {it.hint && <span className="text-xs text-gray-400">{it.hint}</span>}
                </button>
              );
            })}
          </div>,
          document.body
        )}
    </>
  );
}
